import type { Category, Consent } from './types';

const UNBLOCKED_ATTR = 'data-cookiesie-unblocked';

// Whitelist of legal category values for `data-cookie-category`. A blind
// `consent[cat]` read against an untrusted DOM attribute can hit
// Object.prototype keys (e.g. `__proto__`, `constructor`, `toString`) which
// are always truthy, unblocking the script regardless of consent.
const VALID_CATEGORIES: ReadonlySet<Category> = new Set([
  'strictly_necessary',
  'functional',
  'analytics',
  'marketing',
]);

function categoryAllowed(consent: Consent, raw: string | null): boolean {
  if (!raw) return false;
  if (!VALID_CATEGORIES.has(raw as Category)) return false;
  return !!consent[raw as Category];
}

// mvp_b_plan.md §4.1: <script type="text/plain"> is inert; on consent we
// clone it as a real script tag (mutating `type` on the original would not
// re-execute it). <iframe data-cookie-src="…"> swaps src into place.
export function unblock(consent: Consent): void {
  unblockScripts(consent);
  unblockIframes(consent);
}

function unblockScripts(consent: Consent): void {
  const nodes = document.querySelectorAll<HTMLScriptElement>(
    'script[type="text/plain"][data-cookie-category]',
  );
  nodes.forEach((node) => {
    if (node.hasAttribute(UNBLOCKED_ATTR)) return;
    if (!categoryAllowed(consent, node.getAttribute('data-cookie-category'))) return;
    const fresh = document.createElement('script');
    // Per the HTML spec, dynamically-created script elements have the
    // "force async" flag set, so external scripts execute in *completion*
    // order, not DOM order. For pairs like jQuery + a jQuery plugin or
    // gtag.js + a gtag('config', ...) call, that produces intermittent
    // ReferenceErrors ("$ is not defined") that disappear without the
    // snippet. Clearing `async` via the IDL property forces insertion-order
    // execution among dynamically-inserted scripts (setAttribute('async')
    // has no effect on the force-async flag).
    fresh.async = false;
    // Copy attributes, except `type` (so it executes) and our marker.
    for (const a of Array.from(node.attributes)) {
      if (a.name === 'type') continue;
      fresh.setAttribute(a.name, a.value);
    }
    if (node.textContent) fresh.textContent = node.textContent;
    fresh.setAttribute(UNBLOCKED_ATTR, '1');
    node.setAttribute(UNBLOCKED_ATTR, '1');
    node.parentNode?.insertBefore(fresh, node.nextSibling);
  });
}

function unblockIframes(consent: Consent): void {
  const nodes = document.querySelectorAll<HTMLIFrameElement>(
    'iframe[data-cookie-src][data-cookie-category]',
  );
  nodes.forEach((node) => {
    if (node.hasAttribute(UNBLOCKED_ATTR)) return;
    const src = node.getAttribute('data-cookie-src');
    if (!src) return;
    if (!categoryAllowed(consent, node.getAttribute('data-cookie-category'))) return;
    node.setAttribute('src', src);
    node.setAttribute(UNBLOCKED_ATTR, '1');
  });
}
